import React, { useState } from "react";
import { Download, Copy, Check, ExternalLink, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

// Full markdown content lives at /app/CHECKLIST_CREDENZIALI_CRM.md — kept in sync manually.
const RAW_MD = `# Checklist Credenziali — CRM Perla Music (Fase 2)

Documento da condividere con Alessio per raccogliere in anticipo tutte le
chiavi/credenziali necessarie ad attivare le integrazioni delle Milestone 2‑5
del CRM.

Legenda:
- Facile — Alessio può ottenerlo in autonomia in pochi minuti
- Medio — Serve un account attivo e qualche passaggio di setup
- Complesso — Richiede approvazione Meta, revisione app, o account business

---

## Milestone 2 — Google Calendar OAuth (per Venditori)

A cosa serve: ogni venditore, dopo aver preso appuntamento con un lead,
crea in automatico un evento nel proprio Google Calendar (o vede gli eventi
Meet). Anche i reminder e i no‑show partono da qui.

### 1. Google Cloud OAuth Client (difficoltà: Medio)
Chi lo crea: Alessio (una tantum) su Google Cloud Console
https://console.cloud.google.com/

Passaggi:
1. Creare un nuovo progetto "Perla Music CRM"
2. Abilitare Google Calendar API
3. In OAuth consent screen → configurare l'app come Interno se avete
   Workspace, altrimenti Esterno + aggiungere Alessio + venditori come
   test users iniziali
4. In Credentials → creare OAuth 2.0 Client ID → tipo Web application
5. Authorized redirect URI: https://<dominio-finale>/api/crm/google/callback

Ci servono:
- GOOGLE_CLIENT_ID = _________________________________________________
- GOOGLE_CLIENT_SECRET = _____________________________________________
- GOOGLE_REDIRECT_URI = (te lo diamo noi, dipende dal dominio)

Ogni venditore poi darà il proprio consenso al primo accesso (nessuna
chiave da consegnarci lato singolo utente).

---

## Milestone 3 — Meta Ads (Facebook/Instagram) Lead Ingestion

A cosa serve: quando qualcuno compila un modulo dentro un ad
Facebook/Instagram di Modulo istantaneo, il lead entra automaticamente nel
CRM con tutti i parametri UTM e viene assegnato in round‑robin.

### 2. Meta Business & App (difficoltà: Complesso)
Chi lo crea: Alessio + agenzia ads su https://developers.facebook.com/

Passaggi:
1. Su business.facebook.com confermare che la Pagina Facebook di Perla
   Music è dentro un Business Manager
2. Su Meta for Developers creare un'app di tipo Business "Perla Music CRM"
3. Aggiungere prodotto Webhooks + Leads Retrieval
4. Assegnare l'app al Business Manager di Perla Music
5. Configurare i permessi leads_retrieval + pages_manage_metadata
6. Configurare il Webhook che punta a https://<dominio>/api/crm/meta/webhook

Ci servono:
- META_APP_ID = _______________________________________________________
- META_APP_SECRET = ___________________________________________________
- META_PAGE_ACCESS_TOKEN (long‑lived, per la Pagina Perla Music) = ____
- META_VERIFY_TOKEN (stringa random che decidi tu) = __________________
- ID Pagina Facebook Perla Music = ____________________________________

> Il long‑lived Page Access Token si genera da Graph API Explorer partendo
> da un token utente e va rinnovato ogni ~60 giorni (lo automatizzeremo).

### 3. Verifica Ownership Dominio (difficoltà: Medio)
Verifica del dominio finale via file HTML o DNS TXT (te lo diamo noi
quando ci comunichi il dominio finale).

---

## Milestone 4 — Meta Ads Insights (Dashboard Spesa + ROI)

A cosa serve: vediamo quanto abbiamo speso in ads vs quanto abbiamo
fatturato dai lead di quella campagna → ROAS reale.

Riusiamo la stessa Meta App del punto 2 ma con permessi aggiuntivi:
- ads_read
- ads_management (opzionale, solo se volete pausare/lanciare campagne dal CRM)

Ci serve:
- Ad Account ID di Perla Music (formato act_XXXXXXXXXX) = _____________

---

## Milestone 5 — WhatsApp Cloud API (Meta)

A cosa serve:
- Reminder automatici pre‑appuntamento
- Follow‑up automatici su lead "Contattato" fermi da >24h
- Chat 1‑a‑1 dei venditori con i lead direttamente dentro il CRM

### 4. WhatsApp Business Account (difficoltà: Complesso)
Chi lo crea: Alessio su business.facebook.com seguendo la procedura
Cloud API di Meta.

Passaggi:
1. Nella Meta App del punto 2 → aggiungere prodotto WhatsApp
2. Collegare o creare un WhatsApp Business Account (WABA)
3. Aggiungere/verificare un numero di telefono dedicato a Perla Music
4. Approvare l'ID Aziendale (verified business) — può richiedere qualche
   giorno lato Meta
5. Creare almeno 1 template messaggio approvato per i reminder

Ci servono:
- WHATSAPP_PHONE_NUMBER_ID = __________________________________________
- WHATSAPP_BUSINESS_ACCOUNT_ID = ______________________________________
- WHATSAPP_ACCESS_TOKEN (permanent system user token) = _______________
- WHATSAPP_VERIFY_TOKEN (stringa random che decidi tu) = ______________
- Numero telefono ufficiale WhatsApp Perla Music (+39...) = ___________

### Costi WhatsApp Cloud API (informativo)
- Primi 1.000 messaggi/mese service gratuiti
- Messaggi marketing/utility ~0.05–0.08 € l'uno in Italia

---

## Milestone extra — Notifiche Email Automatiche

Due opzioni:

### Opzione A — Emergent LLM key (già inclusa) via SMTP interno
Semplice, gratis, ma dominio "generic".

### Opzione B — Resend (consigliato)
resend.com → dominio verificato @perlamusicmgmt.com con SPF/DKIM/DMARC,
3.000 email/mese gratis.

Se scegliete Resend:
- RESEND_API_KEY = ____________________________________________________
- Dominio email di invio (es. noreply@perlamusicmgmt.com) = ___________

---

## Ricapitolando: cosa serve chiedere ad Alessio in blocco

1. Google Cloud OAuth Client → Client ID + Client Secret
2. Meta App di tipo Business → App ID, App Secret, Page Access Token, ID Pagina, Ad Account ID
3. WhatsApp Business Account con numero dedicato → Phone Number ID, WABA ID, Access Token, numero
4. Dominio finale definitivo
5. (Opzionale) Account Resend

---

## Timing consigliato

| Milestone | Chi crea | Tempo Alessio | Tempo Meta review |
|-----------|----------|---------------|-------------------|
| Google Cloud OAuth | Alessio | ~30 min | zero |
| Meta App + Page Token | Alessio + agenzia | ~1h | 0–3 giorni |
| Dominio verification | Alessio | ~15 min (DNS) | zero |
| WhatsApp Cloud API | Alessio + Meta | ~1h + attesa | 3–10 giorni |
| Resend (opzionale) | Alessio | ~15 min + DNS | zero |
`;

const DIFF_COLOR = { easy: "text-emerald-600", medium: "text-amber-600", hard: "text-red-600" };

const SECTIONS = [
  {
    id: "m2",
    milestone: "Milestone 2",
    title: "Google Calendar OAuth (per Venditori)",
    purpose: "Ogni venditore, dopo aver preso appuntamento con un lead, crea in automatico un evento nel proprio Google Calendar. Reminder e no‑show partono da qui.",
    items: [
      {
        difficulty: "medium",
        label: "Google Cloud OAuth Client",
        owner: "Alessio (una tantum)",
        link: { url: "https://console.cloud.google.com/", label: "Google Cloud Console" },
        steps: [
          "Creare un nuovo progetto 'Perla Music CRM'",
          "Abilitare Google Calendar API",
          "In OAuth consent screen → Interno (se avete Workspace) oppure Esterno + aggiungere Alessio + venditori come test users",
          "In Credentials → creare OAuth 2.0 Client ID → tipo Web application",
          "Aggiungere DUE Authorized redirect URI: (a) preview dev — https://perla-mgmt-stage.preview.emergentagent.com/api/crm/google/callback (b) produzione — https://<dominio-finale>/api/crm/google/callback",
        ],
        keys: [
          { code: "GOOGLE_CLIENT_ID", note: "" },
          { code: "GOOGLE_CLIENT_SECRET", note: "" },
        ],
        footer: "Ogni venditore darà il proprio consenso al primo accesso dalla sezione /crm/impostazioni. Nessuna chiave singola per utente. Scope richiesti: calendar.events + userinfo.email.",
      },
    ],
  },
  {
    id: "m3",
    milestone: "Milestone 3",
    title: "Meta Ads — Lead Ingestion automatica",
    purpose: "Quando qualcuno compila un modulo dentro un ad Facebook/Instagram, il lead entra automaticamente nel CRM con UTM completi e viene assegnato in round‑robin.",
    items: [
      {
        difficulty: "hard",
        label: "Meta Business & App",
        owner: "Alessio + agenzia ads",
        link: { url: "https://developers.facebook.com/", label: "Meta for Developers" },
        steps: [
          "Su business.facebook.com confermare che la Pagina Facebook di Perla Music è dentro un Business Manager",
          "Su Meta for Developers creare un'app di tipo Business 'Perla Music CRM'",
          "Aggiungere prodotto Webhooks + Leads Retrieval",
          "Assegnare l'app al Business Manager di Perla Music",
          "Permessi: leads_retrieval + pages_manage_metadata",
          "Configurare Webhook → https://<dominio>/api/crm/meta/webhook",
        ],
        keys: [
          { code: "META_APP_ID", note: "" },
          { code: "META_APP_SECRET", note: "" },
          { code: "META_PAGE_ACCESS_TOKEN", note: "long‑lived, per la Pagina Perla Music" },
          { code: "META_VERIFY_TOKEN", note: "stringa random che decidi tu (es. perla-2026-xyz)" },
          { code: "ID Pagina Facebook", note: "numerico" },
        ],
        footer: "Il long‑lived Page Access Token va rinnovato ogni ~60 giorni: lo automatizzeremo.",
      },
      {
        difficulty: "medium",
        label: "Verifica Ownership Dominio",
        owner: "Alessio",
        steps: ["Verifica del dominio finale via file HTML o record DNS TXT (te lo forniamo noi quando ci comunichi il dominio finale)"],
        keys: [],
      },
    ],
  },
  {
    id: "m4",
    milestone: "Milestone 4",
    title: "Meta Ads Insights (Dashboard Spesa + ROI)",
    purpose: "Dashboard admin con quanto speso in ads vs fatturato dai lead di quella campagna → ROAS reale.",
    items: [
      {
        difficulty: "easy",
        label: "Riuso della stessa Meta App con permessi aggiuntivi",
        owner: "Alessio",
        steps: [
          "Aggiungere permesso ads_read alla Meta App",
          "Opzionale: ads_management (solo se in futuro volete pausare/lanciare campagne dal CRM)",
        ],
        keys: [
          { code: "Ad Account ID", note: "formato act_XXXXXXXXXX" },
        ],
      },
    ],
  },
  {
    id: "m5",
    milestone: "Milestone 5",
    title: "WhatsApp Cloud API (Meta)",
    purpose: "Reminder automatici pre‑appuntamento, follow‑up automatici, chat 1‑a‑1 dei venditori con i lead dentro il CRM (numero unico ufficiale).",
    items: [
      {
        difficulty: "hard",
        label: "WhatsApp Business Account",
        owner: "Alessio + Meta",
        link: { url: "https://business.facebook.com/", label: "Business Manager" },
        steps: [
          "Nella Meta App aggiungere prodotto WhatsApp",
          "Collegare/creare un WhatsApp Business Account (WABA)",
          "Aggiungere e verificare un numero di telefono dedicato a Perla Music (non può essere già su WhatsApp personale)",
          "Approvazione ID Aziendale (verified business) — 3-10 giorni lato Meta",
          "Creare almeno 1 template messaggio approvato per i reminder (es. 'Ciao {{1}}, ti ricordo l'appuntamento del {{2}}. — Perla Music')",
        ],
        keys: [
          { code: "WHATSAPP_PHONE_NUMBER_ID", note: "" },
          { code: "WHATSAPP_BUSINESS_ACCOUNT_ID", note: "" },
          { code: "WHATSAPP_ACCESS_TOKEN", note: "permanent system user token" },
          { code: "WHATSAPP_VERIFY_TOKEN", note: "stringa random che decidi tu" },
          { code: "Numero telefono WhatsApp Perla Music", note: "formato +39..." },
        ],
        footer: "Costi indicativi: primi 1.000 msg/mese service gratuiti, marketing/utility ~0,05–0,08 € l'uno in Italia. Meta fattura sulla carta collegata al Business Manager.",
      },
    ],
  },
  {
    id: "extra-email",
    milestone: "Extra",
    title: "Notifiche Email Automatiche",
    purpose: "Alert email al venditore (lead fermo >24h), notifica no‑show, riepilogo settimanale.",
    items: [
      {
        difficulty: "easy",
        label: "Opzione A — Emergent LLM key (già inclusa) via SMTP interno",
        owner: "Nessun setup",
        steps: ["Zero setup necessario: sfruttiamo l'infrastruttura Emergent. Deliverability media, dominio generic."],
        keys: [],
      },
      {
        difficulty: "medium",
        label: "Opzione B — Resend con dominio Perla (consigliato)",
        owner: "Alessio",
        link: { url: "https://resend.com", label: "Resend" },
        steps: [
          "Registrarsi su resend.com",
          "Verificare il dominio perlamusicmgmt.com aggiungendo record SPF + DKIM + DMARC (te li forniamo noi)",
          "3.000 email/mese gratis, poi 0,001 €/mail",
        ],
        keys: [
          { code: "RESEND_API_KEY", note: "" },
          { code: "Dominio email di invio", note: "es. noreply@perlamusicmgmt.com" },
        ],
      },
    ],
  },
];

const TIMING = [
  { milestone: "Google Cloud OAuth", who: "Alessio", timeAlessio: "~30 min", timeMeta: "zero" },
  { milestone: "Meta App + Page Token", who: "Alessio + agenzia", timeAlessio: "~1h", timeMeta: "0–3 giorni" },
  { milestone: "Dominio verification", who: "Alessio", timeAlessio: "~15 min (DNS)", timeMeta: "zero" },
  { milestone: "WhatsApp Cloud API", who: "Alessio + Meta", timeAlessio: "~1h + attesa", timeMeta: "3–10 giorni" },
  { milestone: "Resend (opzionale)", who: "Alessio", timeAlessio: "~15 min + DNS", timeMeta: "zero" },
];

const DifficultyBadge = ({ level }) => {
  const label = { easy: "Facile", medium: "Medio", hard: "Complesso" }[level] || level;
  const color = DIFF_COLOR[level] || "text-[#0A0A0A]/60";
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold ${color}`}>● {label}</span>
  );
};

export default function AdminCredentialsChecklist() {
  const [open, setOpen] = useState(() => Object.fromEntries(SECTIONS.map((s) => [s.id, true])));

  const download = () => {
    const blob = new Blob([RAW_MD], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "checklist-credenziali-crm-perla-music.md";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Checklist scaricata");
  };

  const copyEmailPrompt = async () => {
    const text = `Ciao Alessio, per attivare tutte le integrazioni del CRM ci servono questi accessi/credenziali:

1. Google Cloud OAuth Client (per Google Calendar dei venditori)
   → Client ID + Client Secret

2. Meta App di tipo Business con permessi leads_retrieval, pages_manage_metadata, ads_read, whatsapp_business_messaging
   → App ID, App Secret, Page Access Token (long-lived) della Pagina Facebook Perla Music, ID Pagina, Ad Account ID

3. WhatsApp Business Account con numero dedicato Perla Music
   → Phone Number ID, WABA ID, Access Token permanente, numero telefono

4. Dominio finale definitivo da collegare al deploy (es. crm.perlamusicmgmt.com)

5. (Opzionale) Account Resend per invio email transazionali dal dominio Perla Music

Una volta ottenute, consegnale solo tramite canale sicuro (non incollarle in chat pubbliche).`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Testo copiato negli appunti — pronto da girare ad Alessio");
    } catch {
      toast.error("Impossibile copiare — provala manualmente");
    }
  };

  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  return (
    <div className="p-6 lg:p-10 max-w-5xl" data-testid="admin-credentials-checklist-page">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— Fase 2 CRM · Setup</div>
        <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2">Credenziali CRM</h1>
        <p className="text-[#0A0A0A]/70 mt-3 max-w-3xl leading-relaxed">
          Lista completa di tutte le chiavi/credenziali che servono per attivare le Milestone 2–5 del CRM
          (Google Calendar, Meta Ads, WhatsApp, notifiche email). Girala ad Alessio così può iniziare a
          preparare gli accessi in parallelo mentre completiamo lo sviluppo.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-8">
        <button onClick={download} className="btn-cta py-2 px-4 text-xs" data-testid="checklist-download-btn">
          <Download size={14} /> Scarica .md
        </button>
        <button onClick={copyEmailPrompt} className="btn-outline-dark py-2 px-4 text-xs" data-testid="checklist-copy-email-btn">
          <Copy size={14} /> Copia messaggio per Alessio
        </button>
      </div>

      {/* Legenda */}
      <div className="mb-8 border-2 border-[#0A0A0A] bg-[#FAFAF7] p-4 flex flex-wrap gap-6 text-xs">
        <div className="flex items-center gap-2"><span className="text-emerald-600 text-base leading-none">●</span> <span className="uppercase tracking-widest font-bold">Facile</span> — in autonomia in pochi minuti</div>
        <div className="flex items-center gap-2"><span className="text-amber-600 text-base leading-none">●</span> <span className="uppercase tracking-widest font-bold">Medio</span> — account attivo + setup</div>
        <div className="flex items-center gap-2"><span className="text-red-600 text-base leading-none">●</span> <span className="uppercase tracking-widest font-bold">Complesso</span> — approvazione Meta / verifica business</div>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {SECTIONS.map((sec) => (
          <div key={sec.id} className="border-2 border-[#0A0A0A] bg-[#FAFAF7]" data-testid={`checklist-section-${sec.id}`}>
            <button onClick={() => toggle(sec.id)} className="w-full flex items-start justify-between p-5 text-left hover:bg-[#F2F2EC] transition-colors">
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">{sec.milestone}</div>
                <h2 className="font-display text-2xl md:text-3xl uppercase text-[#0A0A0A] mt-1">{sec.title}</h2>
                <p className="text-sm text-[#0A0A0A]/70 mt-2 max-w-3xl">{sec.purpose}</p>
              </div>
              <div className="pt-2 pl-3 shrink-0">{open[sec.id] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}</div>
            </button>

            {open[sec.id] && (
              <div className="px-5 pb-5 space-y-5 border-t-2 border-[#0A0A0A]/10">
                {sec.items.map((it, idx) => (
                  <div key={idx} className="pt-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <h3 className="font-bold text-lg text-[#0A0A0A]">{it.label}</h3>
                        <div className="mt-1 flex items-center gap-3 flex-wrap">
                          <DifficultyBadge level={it.difficulty} />
                          <span className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">Owner: <strong className="text-[#0A0A0A]">{it.owner}</strong></span>
                        </div>
                      </div>
                      {it.link && (
                        <a href={it.link.url} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-widest font-bold text-[#FF5A1F] hover:underline flex items-center gap-1" data-testid={`checklist-link-${sec.id}-${idx}`}>
                          {it.link.label} <ExternalLink size={12} />
                        </a>
                      )}
                    </div>

                    {it.steps.length > 0 && (
                      <ol className="mt-4 space-y-2 text-sm text-[#0A0A0A]/85">
                        {it.steps.map((s, i) => (
                          <li key={i} className="flex gap-3">
                            <span className="shrink-0 w-6 h-6 border-2 border-[#0A0A0A] flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                            <span>{s}</span>
                          </li>
                        ))}
                      </ol>
                    )}

                    {it.keys.length > 0 && (
                      <div className="mt-4 border-2 border-[#0A0A0A]/20 bg-white p-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F] mb-2">Ci servono queste chiavi</div>
                        <ul className="space-y-1.5 text-sm font-mono">
                          {it.keys.map((k, i) => (
                            <li key={i} className="flex items-baseline gap-3">
                              <Check size={12} className="text-[#0A0A0A]/40 shrink-0 translate-y-0.5" />
                              <span className="font-bold">{k.code}</span>
                              {k.note && <span className="text-xs text-[#0A0A0A]/60 font-sans">— {k.note}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {it.footer && (
                      <div className="mt-3 border-l-2 border-[#FF5A1F] pl-3 text-xs text-[#0A0A0A]/70 italic">
                        {it.footer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Timing table */}
      <div className="mt-10">
        <h2 className="font-display text-3xl uppercase text-[#0A0A0A] mb-4">Timing consigliato</h2>
        <div className="border-2 border-[#0A0A0A] bg-[#FAFAF7] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-[#FAFAF7]">
              <tr>
                <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Milestone</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Chi</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Tempo Alessio</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Attesa Meta / DNS</th>
              </tr>
            </thead>
            <tbody>
              {TIMING.map((t) => (
                <tr key={t.milestone} className="border-t border-[#0A0A0A]/10">
                  <td className="px-4 py-3 font-bold">{t.milestone}</td>
                  <td className="px-4 py-3 text-[#0A0A0A]/70">{t.who}</td>
                  <td className="px-4 py-3 text-[#FF5A1F] font-bold">{t.timeAlessio}</td>
                  <td className="px-4 py-3 text-[#0A0A0A]/70">{t.timeMeta}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-10 border-2 border-[#FF5A1F] bg-[#FF5A1F]/5 p-5">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">Consiglio operativo</div>
        <p className="mt-2 text-sm text-[#0A0A0A]/85 leading-relaxed">
          Iniziare <strong>subito</strong> con WhatsApp Cloud API (il più lento in approvazione Meta, 3–10 giorni)
          e con Google Calendar OAuth (immediato). Le altre integrazioni possono partire in parallelo. Il dominio
          finale va scelto e verificato appena possibile perché blocca sia Meta Ads webhook sia gli URI di callback OAuth.
        </p>
      </div>
    </div>
  );
}
